import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  useTheme,
} from "@mui/material";
import Fab from "@mui/material/Fab";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import useDataGridScrollRestore from "hooks/useDataGridScrollRestore";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";

const RequestorGroupsTab = ({ clientId }) => {
  const theme = useTheme();

  const {
    setOverlayData,
    setOverlayName,
    showSnackbar,
    openDialog,
    closeDialog,
    triggerPageRefresh,
    refreshPage,
  } = useStateContext();

  const [groups, setGroups] = useState([]);
  const [requestors, setRequestors] = useState([]);
  const [loading, setLoading] = useState(true);

  const apiRef = useGridApiRef();
  // Preserve the scroll position across refetches (saves bounce to the top)
  const { capture: captureGridScroll, restore: restoreGridScroll } =
    useDataGridScrollRestore(apiRef);

  const [groupContextMenu, setGroupContextMenu] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const loadData = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    captureGridScroll();
    try {
      const [groupsResponse, clientResponse] = await Promise.all([
        api.get(`/requestorGroups/client/${clientId}`),
        api.get(`/clients/${clientId}`),
      ]);
      const groupsData = groupsResponse.data || [];
      setGroups(groupsData);
      restoreGridScroll(groupsData);
      setRequestors(clientResponse.data?.Requestors || []);
    } catch (error) {
      console.error("Error loading requestor groups:", error);
      showSnackbar("Failed to load requestor groups", theme, "error");
    } finally {
      setLoading(false);
    }
  }, [clientId, showSnackbar, theme, captureGridScroll, restoreGridScroll]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshPage]);

  const openCreateOverlay = () => {
    setOverlayData({
      ClientID: clientId,
      Requestors: requestors,
      Groups: groups,
      Action: OVERLAY_NAMES.REQUESTOR_GROUP_ACTION_NEW,
    });
    setOverlayName(OVERLAY_NAMES.REQUESTOR_GROUP_NEW_EDIT);
  };

  const openEditOverlay = (group) => {
    setOverlayData({
      ClientID: clientId,
      Requestors: requestors,
      Groups: groups,
      Group: group,
      Action: OVERLAY_NAMES.REQUESTOR_GROUP_ACTION_EDIT,
    });
    setOverlayName(OVERLAY_NAMES.REQUESTOR_GROUP_NEW_EDIT);
  };

  const handleDelete = (group) => {
    openDialog({
      theme: theme,
      title: "Delete Confirmation",
      message: `Are you sure you'd like to delete Group: ${group?.Name}?`,
      customContent: null,
      tertiaryAction: null,
      secondaryAction: "Close",
      primaryAction: "Delete",
      tertiaryOnClick: null,
      secondaryOnClick: () => closeDialog(),
      primaryOnClick: async () => {
        closeDialog();
        try {
          await api.delete(`/requestorGroups/${group.id}`);
          showSnackbar("Group deleted successfully", theme, "success");
          await loadData();
          triggerPageRefresh("Requestor groups updated", theme);
        } catch (error) {
          console.error("Error deleting requestor group:", error);
          showSnackbar(
            error.response?.data?.error || "Failed to delete requestor group",
            theme,
            "error",
          );
        }
      },
    });
  };

  const handleGroupGridContextMenu = (event) => {
    event.preventDefault();

    const groupId = Number(event.currentTarget.getAttribute("data-id"));
    if (!groupId) return;

    const group = (groups || []).find(
      (item) => Number(item.id) === groupId,
    );
    if (!group) return;

    setGroupContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
    setSelectedGroup(group);
  };

  const handleGroupMenuClose = () => {
    setGroupContextMenu(null);
    setSelectedGroup(null);
  };

  const groupColumns = useMemo(
    () => [
      {
        field: "Name",
        headerName: "Name",
        minWidth: 180,
        flex: 1.2,
        valueGetter: (_, row) => row?.Name || "N/A",
      },
      {
        field: "Members",
        headerName: "Members",
        minWidth: 220,
        flex: 1.6,
        valueGetter: (_, row) =>
          (row?.Members || []).map((m) => m?.Name).filter(Boolean).join(", ") ||
          "—",
      },
      {
        field: "PrimaryInvoicer",
        headerName: "Primary Invoicer",
        minWidth: 160,
        flex: 1.1,
        valueGetter: (_, row) =>
          row?.PrimaryInvoicer?.Name || "None (defaults to Requestor)",
      },
      {
        field: "CCRequestors",
        headerName: "CC",
        minWidth: 180,
        flex: 1.2,
        valueGetter: (_, row) =>
          (row?.CCRequestors || [])
            .map((cc) => cc?.Name)
            .filter(Boolean)
            .join(", ") || "—",
      },
    ],
    [],
  );

  return (
    <>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          p: "0.25rem",
        }}
      >
        <DataGrid
          rows={groups || []}
          columns={groupColumns}
          getRowId={(row) => row.id}
          apiRef={apiRef}
          density="compact"
          disableRowSelectionOnClick
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
            sorting: {
              sortModel: [{ field: "Name", sort: "asc" }],
            },
          }}
          sx={{
            borderRadius: "12px",
            boxShadow: theme.shadows[3],
            border: `1px solid ${theme.palette.primary.border2}`,
            bgcolor: theme.palette.background.default,

            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },
            "& .MuiDataGrid-menuIconButton": {
              opacity: 0,
            },
            "& .MuiDataGrid-sortIcon": {
              opacity: 0,
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiTypography-root.MuiTypography-body1": {
              textAlign: "left !important",
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus":
              {
                outline: "none !important",
              },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
          slotProps={{
            row: {
              onContextMenu: handleGroupGridContextMenu,
              style: { cursor: "context-menu" },
            },
          }}
        />
      </Box>

      <Menu
        open={groupContextMenu !== null}
        onClose={handleGroupMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          groupContextMenu !== null
            ? {
                top: groupContextMenu.mouseY,
                left: groupContextMenu.mouseX,
              }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            openEditOverlay(selectedGroup);
            handleGroupMenuClose();
          }}
        >
          <ListItemIcon>
            <EditRoundedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Edit</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDelete(selectedGroup);
            handleGroupMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteRoundedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Delete</Typography>
        </MenuItem>
      </Menu>

      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: "absolute",
          bottom: 90,
          right: 40,
          zIndex: 2,
          borderRadius: "12px",
          width: 48,
          height: 48,
        }}
        onClick={openCreateOverlay}
      >
        <AddRoundedIcon
          sx={{
            color: theme.palette.background.default,
            fontSize: 24,
          }}
        />
      </Fab>
    </>
  );
};

export default RequestorGroupsTab;
